/**
 * `Alert` — admin's TWO inline-callout dialects, as a skin over the shared
 * `@auctogon/ui` Alert (BL-1521, ratifying the `Alert` row of the ADR 030
 * Canon).
 *
 * ADR 030 § 3c canon: **extend this; never fork.** It lived under
 * `components/settings-v2/` until BL-1463, which is a large part of why the
 * clerk console grew its own — a component filed under a section name reads
 * as belonging to that section, and the next surface writes its own instead.
 * It is not settings-specific and never was.
 *
 * Structure — the flex row, the icon slot, the title/body stack, the dismiss ×,
 * and the rule that a `danger` banner is ALWAYS `role="alert"` — lives in the
 * shared primitive. This file binds admin's two appearances and nothing else:
 *
 *   - `v2` — the settings-shell `ui-alert` CSS family (`styles/settings-v2.css`)
 *     and its four tone modifiers. The default, so the 60 pre-existing callers
 *     compile AND render unchanged. Only styled where a route layout imports
 *     settings-v2.css (customers / settings / super-admin / inventory /
 *     auctions / liens / clerk).
 *   - `classic` — the Design-approved Tailwind treatment (Dan Saper,
 *     2026-09-10): `p-3` · `rounded-lg` · `text-sm` · red-700 on red-50 with a
 *     red-200 border. Works on every route, which is why the 53 hand-rolled
 *     error banners this item deleted (auth, invoices, accounting, approvals —
 *     most of them outside the v2 CSS scope) all bind it.
 *
 * The prop names mirror the bidder app's twin (`apps/bidder-web/src/
 * components/ui/Alert.tsx`) so the two dialects share one API even though
 * their palettes are deliberately different (ADR 030's two-layer model —
 * unifying the *look* would be the regression, unifying the *shape* is the
 * point). Admin keeps `kind` where bidder says `tone`: 60 callers were written
 * against it, and the rename buys nothing a reader can see.
 */

import type { CSSProperties, ReactNode } from 'react';
import { AlertTriangle, CheckCircle2, Info } from 'lucide-react';
import { Alert as UiAlert, type AlertDialect, type AlertTone } from '@auctogon/ui/web';
import { useModalPresentation } from './Modal';

type AlertKind = AlertTone;

/**
 * Which page shell the callout renders inside.
 *
 * `v2` is the settings-v2 shell (`.s-v2`), whose visual language is the
 * CSS-variable `.ui-*` family in settings-v2.css. `classic` is the Tailwind
 * dashboard chrome (auth, invoices, most modals). One component, two dialects
 * — not a fork. Same split `EmptyState` carries (BL-916).
 */
export type AlertVariant = 'v2' | 'classic';

/**
 * The settings-v2 appearance. No `body` slot on purpose: the 60 callers were
 * written against a bare-children DOM, and a wrapper would re-flow the few
 * that pass sibling nodes straight into the flex row.
 */
const ADMIN_V2_DIALECT: AlertDialect = {
  root: 'ui-alert',
  tones: {
    info: 'ui-alert--info',
    success: 'ui-alert--success',
    warning: 'ui-alert--warning',
    danger: 'ui-alert--danger',
  },
  stack: 'space-y-density-tight',
  title: 'font-medium',
  titleAs: 'p',
  bodyAs: 'p',
  dismiss: 'ml-auto shrink-0 rounded p-0.5 opacity-70 hover:opacity-100',
};

/**
 * The classic Tailwind appearance — the one error-banner treatment.
 *
 * Contrast (ADR 051, `node scripts/contrast.mjs`):
 *   red-700 on red-50 ........ 5.91:1  text ✓ (needs 4.5; red-600 was 4.41 ✗)
 *   amber-700 on amber-50 .... 4.84:1  text ✓
 *   green-700 on green-50 .... 4.79:1  text ✓
 *   blue-700 on blue-50 ...... 6.16:1  text ✓
 * The `*-200` border is decorative (1.3:1) — the fill, the text and the
 * wording carry the state, so it is exempt.
 *
 * No margin on the root: callers pass their own spacing via `className`
 * (`mb-4`, `mt-3`, ItemForm's `mb-6`). Because the root declares no margin
 * utility, `clsx` concatenation cannot produce a conflict.
 */
const ADMIN_CLASSIC_DIALECT: AlertDialect = {
  root: 'flex items-start gap-density-cluster rounded-lg border p-3 text-body',
  tones: {
    info: 'bg-blue-50 border-blue-200 text-blue-700',
    success: 'bg-green-50 border-green-200 text-green-700',
    warning: 'bg-amber-50 border-amber-200 text-amber-700',
    danger: 'bg-red-50 border-red-200 text-red-700',
  },
  icon: 'mt-0.5 shrink-0',
  body: 'min-w-0 flex-1',
  stack: 'min-w-0 flex-1',
  title: 'font-semibold mb-0.5',
  dismiss:
    'shrink-0 rounded p-0.5 opacity-70 transition-opacity hover:opacity-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-current',
};

/**
 * The phone sheet's one banner (ADR 063 — Type Scale Amendment 5, BL-2403),
 * whatever dialect the caller asked for: 16px padding, a 20px tone icon, 16/500
 * text, and a title at the text size (a sheet banner has no heading step).
 *
 * Contrast (ADR 051, `node scripts/contrast.mjs`):
 *   --warning-700 #8f5a00 on amber-50 ... 5.58:1  text ✓ (needs 4.5)
 *   red-900 #7f1d1d on red-50 ........... 9.16:1  text ✓
 *   --info-700 #1b538a on blue-50 ....... 7.28:1  text ✓
 *   --success-700 #18653a on green-50 ... 6.77:1  text ✓
 * The `*-100` border is decorative: the fill, icon and wording carry the tone.
 */
const ADMIN_SHEET_DIALECT: AlertDialect = {
  // 12px icon-to-text, measured on the Wave 1 prototype: the control tier.
  root: 'flex items-start gap-density-control rounded-lg border p-4 text-body-lg font-medium',
  tones: {
    info: 'bg-blue-50 border-blue-100 text-[var(--info-700)]',
    success: 'bg-green-50 border-green-100 text-[var(--success-700)]',
    warning: 'bg-amber-50 border-amber-100 text-[var(--warning-700)]',
    danger: 'bg-red-50 border-red-100 text-red-900',
  },
  icon: 'mt-0.5 shrink-0',
  body: 'min-w-0 flex-1',
  stack: 'min-w-0 flex-1',
  title: '',
  dismiss:
    'ml-auto shrink-0 rounded p-0.5 opacity-70 transition-opacity hover:opacity-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-current',
};

/** The sheet banner's icon: one per tone, 20px, so every sheet banner reads the same. */
const SHEET_ICON: Record<AlertTone, ReactNode> = {
  info: <Info className="h-5 w-5" aria-hidden />,
  success: <CheckCircle2 className="h-5 w-5" aria-hidden />,
  warning: <AlertTriangle className="h-5 w-5" aria-hidden />,
  danger: <AlertTriangle className="h-5 w-5" aria-hidden />,
};

const DIALECTS: Record<AlertVariant, AlertDialect> = {
  v2: ADMIN_V2_DIALECT,
  classic: ADMIN_CLASSIC_DIALECT,
};

/** The v2 block margin — an inline style, which no class can beat (see `spacing`). */
const V2_BLOCK_SPACING: CSSProperties = { marginBottom: 'var(--space-6)' };

interface AlertProps {
  kind?: AlertKind;
  /** Which shell the callout renders in. Defaults to `v2`. */
  variant?: AlertVariant;
  children?: React.ReactNode;
  className?: string;
  /**
   * ARIA role for the non-danger kinds — pass "status" for a notice screen
   * readers should announce politely, "note" for a static aside. A `danger`
   * callout is ALWAYS an alert; the shared primitive ignores this prop for it.
   */
  role?: React.AriaRole;
  /**
   * Optional leading icon, rendered verbatim as the first child. Callers pass
   * a fully-classed element (`<AlertTriangle className="mt-0.5 h-4 w-4
   * shrink-0" aria-hidden />`) rather than a component reference, matching the
   * bidder twin.
   */
  icon?: React.ReactNode;
  /** Optional bold first line above the body. */
  title?: React.ReactNode;
  /** When provided, renders a × button that calls this. */
  onDismiss?: () => void;
  /** Emitted as `data-testid` on the callout root. */
  testId?: string;
  /**
   * Outer spacing — `v2` only. `block` (the default) keeps the bottom margin
   * every settings page has always relied on; `none` is for a callout composed
   * inside a modal or dialog body that owns its own rhythm. `classic` carries
   * no built-in margin; pass it in `className`.
   *
   * This is a prop rather than a `className` override on purpose. The margin
   * ships as an inline style, which no class can beat — and admin composes
   * with `clsx`, which concatenates rather than resolves conflicting Tailwind
   * utilities (ADR 030 § 3b; `twMerge` is the open BL-1004), so an `m-0` in
   * `className` would not reliably win even as a class.
   */
  spacing?: 'block' | 'none';
}

/**
 * Admin's callout — `ui-alert` in the settings shell, the classic red box
 * everywhere else, and one banner look inside a phone sheet (BL-2403).
 *
 * @param props - {@link AlertProps}
 * @returns The rendered callout.
 */
export default function Alert({
  kind = 'info',
  variant = 'v2',
  children,
  className,
  role,
  icon,
  title,
  onDismiss,
  testId,
  spacing = 'block',
}: AlertProps) {
  const isSheet = useModalPresentation() === 'sheet';
  return (
    <UiAlert
      tone={kind}
      role={role}
      icon={isSheet ? SHEET_ICON[kind] : icon}
      title={title}
      onDismiss={onDismiss}
      className={className}
      testId={testId}
      dialect={isSheet ? ADMIN_SHEET_DIALECT : DIALECTS[variant]}
      style={!isSheet && variant === 'v2' && spacing === 'block' ? V2_BLOCK_SPACING : undefined}
    >
      {children}
    </UiAlert>
  );
}
